import React from "react";
import { Button } from "@mui/material";
import { isLikelyChunkLoadError } from "../../routing/appRouting";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: Error) => void;
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: (Error & { isChunkLoadError?: boolean }) | null;
  errorInfo: React.ErrorInfo | null;
  resetCount: number;
  lastResetKey: string | undefined;
}

const MAX_RAPID_RESETS = 2;

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, resetCount: 0, lastResetKey: undefined };
  }

  static getDerivedStateFromError(error: Error & { isChunkLoadError?: boolean }) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error);
    console.error("Component stack:", info?.componentStack);
    this.setState({ errorInfo: info });
    if (typeof this.props.onError === "function") {
      this.props.onError(error);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      // Guard against rapid error-recovery-error loops on the same resetKey
      const isSameKeyResetting = this.state.lastResetKey === this.props.resetKey;
      const nextResetCount = isSameKeyResetting ? this.state.resetCount + 1 : 0;

      if (nextResetCount >= MAX_RAPID_RESETS) {
        // Stop resetting -- show persistent error UI
        return;
      }

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        resetCount: nextResetCount,
        lastResetKey: this.props.resetKey,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunkError =
        Boolean(this.state.error?.isChunkLoadError) ||
        isLikelyChunkLoadError(this.state.error);

      // TODO: improve the generic error fallback with a styled card layout (chunk errors already have dedicated UI below)
      return (
        <div style={{ padding: "20px", color: "#fff" }}>
          <h1>{isChunkError ? "Application update detected." : "Something went wrong."}</h1>
          {isChunkError && (
            <div style={{ marginBottom: "10px" }}>
              <p style={{ color: "#ffecb3" }}>
                The app was updated while this tab was open. Refresh to load the latest version.
              </p>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Refresh now
              </Button>
            </div>
          )}
          <details style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
            <summary>Error Details (click to expand)</summary>
            <p style={{ color: "#ff6b6b" }}>
              {this.state.error && this.state.error.toString()}
            </p>
            <p style={{ fontSize: "12px", color: "#aaa" }}>
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </p>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
